import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  /**
   * The media query tracking the visitor's reduced motion preference.
   * @type {MediaQueryList}
   */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('focusin', this.suspend);
    this.addEventListener('focusout', this.resume);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.addEventListener('change', this.#handleReducedMotionChange);

    this.play();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    this.#reducedMotion.removeEventListener('change', this.#handleReducedMotionChange);

    this.suspend();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    if (this.prefersReducedMotion) {
      this.#pinFirstSlide();
      return;
    }

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || this.matches(':focus-within') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get prefersReducedMotion() {
    return this.#reducedMotion.matches;
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    this.#current = current;

    let relativeIndex = current % (this.refs.slides ?? []).length;
    if (relativeIndex < 0) {
      relativeIndex += (this.refs.slides ?? []).length;
    }

    this.refs.slides?.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * Stops playback and leaves the first slide active, so the DOM state matches the
   * single slide that CSS shows under `prefers-reduced-motion: reduce`. The live
   * region is dropped too, because nothing is going to change.
   */
  #pinFirstSlide() {
    this.suspend();
    this.paused = false;
    this.current = 0;
    this.removeAttribute('aria-live');
  }

  /**
   * Suspend the slideshow while the page is hidden, without marking it paused.
   */
  #handleVisibilityChange = () => (document.hidden ? this.suspend() : this.resume());

  /**
   * Start or stop playback when the reduced motion preference changes.
   */
  #handleReducedMotionChange = () => {
    this.suspend();
    this.paused = false;

    if (!this.prefersReducedMotion && this.autoplay) {
      this.setAttribute('aria-live', 'polite');
    }

    this.play();
  };
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
